import React from "react";
import { Table, Tag, Button, Space, Tooltip, Typography, Modal } from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  BellOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import dayjs from "dayjs";

const { Text } = Typography;
const { confirm } = Modal;

const PickupListsTable = ({
  pickupLists,
  loading,
  onViewDetails,
  onMarkPrepared,
  onNotifyParent,
}) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("List number copied to clipboard");
  };

  const showMarkPreparedConfirm = (record) => {
    confirm({
      title: "Mark as Prepared?",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to mark pickup list ${record.list_number} as prepared? This means all items have been packed and are ready for collection.`,
      okText: "Yes, Mark as Prepared",
      cancelText: "Cancel",
      onOk() {
        onMarkPrepared(record.id, "");
      },
    });
  };

  const showNotifyParentConfirm = (record) => {
    confirm({
      title: "Notify Parent?",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to notify the parent for ${record.student_name}? They will receive a notification that items are ready for pickup.`,
      okText: "Yes, Notify",
      cancelText: "Cancel",
      onOk() {
        onNotifyParent(record.id);
      },
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      Pending: "orange",
      "In Progress": "processing",
      Ready: "success",
      Notified: "cyan",
      "Partially Collected": "blue",
      Completed: "green",
    };
    return colors[status] || "default";
  };

  const columns = [
    {
      title: "List Number",
      dataIndex: "list_number",
      key: "list_number",
      render: (listNumber) => (
        <Space>
          <Text code>{listNumber}</Text>
          <Tooltip title="Copy">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(listNumber)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Student",
      key: "student",
      render: (_, record) => (
        <div>
          <div>{record.student_name}</div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.student_spc}
          </Text>
        </div>
      ),
    },
    {
      title: "BOTPC",
      dataIndex: "botpc",
      key: "botpc",
      render: (botpc) => <Text code>{botpc}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: "Items",
      dataIndex: "item_count",
      key: "item_count",
      align: "center",
    },
    {
      title: "Generated",
      dataIndex: "generated_at",
      key: "generated_at",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Pickup Date",
      dataIndex: "pickup_date",
      key: "pickup_date",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => onViewDetails(record)}
            />
          </Tooltip>

          {record.status === "Pending" && (
            <Tooltip title="Mark as Prepared">
              <Button
                type="link"
                icon={<CheckOutlined />}
                style={{ color: "#52c41a" }}
                onClick={() => showMarkPreparedConfirm(record)}
              />
            </Tooltip>
          )}

          {record.status === "Ready" && !record.parent_notified_at && (
            <Tooltip title="Notify Parent">
              <Button
                type="link"
                icon={<BellOutlined />}
                style={{ color: "#faad14" }}
                onClick={() => showNotifyParentConfirm(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={pickupLists}
      loading={loading}
      rowKey="id"
      scroll={{ x: 1200 }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} pickup lists`,
      }}
    />
  );
};

export default PickupListsTable;
